import type { Me, MyContext, OrganizationRole } from "@gioia365/contracts";
// Soltanto il sottopercorso delle regole di livello: l'indice del dominio porta nel bundle il dataset dei
// comuni (piano M1b D5), e la velocità dei cambi di schermata è un criterio di accettazione.
import { canCopyRole as domainCanCopyRole, checkRoleAssignment, type AccessActor } from "@gioia365/domain/access";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useMatch, useRouteContext } from "@tanstack/react-router";
import { useMemo } from "react";
import { contextQuery, useMe } from "../api/queries";
import type { TabContext } from "./tab-context";

// Che cosa l'interfaccia mostra nella scheda. Il server resta l'autorità: controlla ogni permesso a ogni
// richiesta, e l'interfaccia usa queste risposte soltanto per nascondere o spiegare.

export type Access = {
  context: MyContext;
  /** L'attore per le regole di `@gioia365/domain/access` (`canActOn`, `checkRoleAssignment`, `canCancelInvitation`, `canManageRole`). */
  actor: AccessActor;
  /** Una funzione o una sezione delle Impostazioni che il server concede in questa scheda. */
  can(feature: string): boolean;
  /** Il Super admin di Gioia365 dentro un'azienda (contesto di piattaforma). */
  isPlatform: boolean;
};

export function accessOf(context: MyContext): Access {
  const features = new Set(context.features);
  const actor: AccessActor =
    context.kind === "platform"
      ? { kind: "platform" }
      : {
          kind: "membership",
          // Il server manda sempre appartenenza e ruolo in questo contesto; senza, nessuna regola passa.
          membershipId: context.membershipId ?? "",
          level: context.role?.level ?? 0,
          isSystem: context.role?.isSystem ?? false,
        };
  return { context, actor, can: (feature) => features.has(feature), isPlatform: context.kind === "platform" };
}

/**
 * Se l'attore della scheda può dare questo ruolo (invito, cambio di ruolo, reinvio): la stessa regola del server
 * (`checkRoleAssignment`, questione 38), cioè mai il ruolo di sistema né un ruolo del suo livello
 * (`isSuperAdminRole`), poi livello più basso del suo e poteri dentro i suoi, salvo il ruolo di sistema e la
 * piattaforma. I poteri sono quelli del contesto della scheda: per un ruolo non di sistema le funzioni effettive
 * sono quelle salvate nel ruolo che la piattaforma non ha spento, mai più larghe di quelle che il server
 * confronta, così l'interfaccia non offre ciò che il server rifiuta.
 */
export function canGiveRole(access: Access, role: OrganizationRole): boolean {
  return checkRoleAssignment(access.actor, powersOf(access), role).ok;
}

/**
 * Se l'attore può copiare questo ruolo in uno su misura, o modificarlo («Nuovo ruolo», «Duplica», «Ruolo su
 * misura», l'editor): `canCopyRole` del dominio, come il server. Mai il ruolo di sistema né un ruolo del suo
 * livello (il Super admin è uno solo, questione 38); poi livello sotto il suo (R16) e poteri dentro i suoi.
 */
export function canCopyRole(access: Access, role: OrganizationRole): boolean {
  return domainCanCopyRole(access.actor, powersOf(access), role);
}

/** I poteri dell'attore nella scheda: quelli che il server confronta con un ruolo. */
function powersOf(access: Access) {
  return { features: access.context.features, areaPermissions: access.context.areaPermissions };
}

/**
 * I ruoli da offrire in una scelta (invito, cambio di ruolo), nell'ordine del server. Il ruolo Super admin, o
 * un ruolo del suo livello, non si offre mai, nemmeno al Super admin né alla piattaforma (lo esclude
 * `canGiveRole`): il Super admin è il titolare ed è uno solo (questione 38). Né un ruolo archiviato.
 */
export function assignableRoles(access: Access, roles: readonly OrganizationRole[]): OrganizationRole[] {
  return roles.filter((role) => role.archivedAt === null && canGiveRole(access, role));
}

export type LeadAction = "view" | "create" | "update" | "delete";
export type LeadScope = "none" | "own" | "all";

const SCOPE_RANK: Readonly<Record<LeadScope, number>> = { none: 0, own: 1, all: 2 };

function scopeOf(value: unknown): LeadScope {
  return value === "all" || value === "own" ? value : "none";
}

/**
 * L'ambito che la scheda ha su un'azione dei Lead, con la stessa regola del server (`leadScope` in
 * `apps/server/src/leads/access.ts`): la piattaforma e il ruolo di sistema hanno tutto; per gli altri vale il ruolo, e
 * mai più della lettura: chi non vede nessun Lead non ne crea, e modifica ed eliminazione non superano la lettura.
 * Serve soltanto a decidere cosa mostrare (per esempio «Nuovo Lead» con la creazione `all`): il server decide.
 */
export function leadScopeOf(access: Access, action: LeadAction): LeadScope {
  return areaScopeOf(access, "leads", action);
}

/**
 * L'ambito che la scheda ha su un'azione delle Smart List (area `smart_lists`), con la stessa regola del server
 * (`smartListScope` in `apps/server/src/smart-lists/access.ts`, uguale a quella dei Lead): serve a mostrare la voce
 * «Smart List» (`view`), «Crea Smart List da questi filtri» (`create`) e «I Lead di un Commerciale, per lui»
 * (`update: all`). Il server decide.
 */
export function smartListScopeOf(access: Access, action: LeadAction): LeadScope {
  return areaScopeOf(access, "smart_lists", action);
}

/**
 * L'ambito dei report (area `reports`, soltanto la lettura), con la stessa regola del server (`reportsScope` in
 * `apps/server/src/today/service.ts`): serve alla Home per sapere se chiedere «Oggi» e «Sequenze rispettate oggi». Si passa
 * l'accesso del menu della scheda (`useMenuSubject`), così nella «Guarda come…» decide la persona osservata, come sul server.
 */
export function reportsScopeOf(access: Access): LeadScope {
  return areaScopeOf(access, "reports", "view");
}

/** La regola comune delle aree: piattaforma e ruolo di sistema tutto; creare vuole la lettura; modifica ed eliminazione mai oltre la lettura. */
function areaScopeOf(access: Access, area: "leads" | "smart_lists" | "reports", action: LeadAction): LeadScope {
  if (access.isPlatform) return "all";
  return roleAreaScope(access.context.role, access.context.areaPermissions, area, action);
}

/** La stessa regola sul ruolo e sui permessi di una persona: quella della scheda o, nella «Guarda come…», quella osservata. */
function roleAreaScope(role: { isSystem: boolean } | null, areaPermissions: MyContext["areaPermissions"], area: string, action: LeadAction): LeadScope {
  if (role?.isSystem) return "all";
  const permissions = areaPermissions[area] ?? {};
  const requested = scopeOf(permissions[action]);
  if (action === "view") return requested;
  const view = scopeOf(permissions.view);
  if (action === "create") return view === "none" ? "none" : requested;
  return SCOPE_RANK[requested] <= SCOPE_RANK[view] ? requested : view;
}

const narrowest = (scope: LeadScope, next: LeadScope): LeadScope => (SCOPE_RANK[next] < SCOPE_RANK[scope] ? next : scope);

/**
 * Se la scheda può leggere o modificare un Lead con questo owner (`canActOnLead` del server): lo deve permettere il perimetro di
 * chi guarda e, nella «Guarda come…», anche quello della persona osservata; `all` sempre, `own` soltanto per i Lead di quella
 * persona. Serve a «Conversazioni», dove una telefonata può stare su un Lead che intanto è passato a un collega. Il server decide.
 */
export function canActOnLeadOf(access: Access, action: "view" | "update", ownerMembershipId: string | null): boolean {
  const { context } = access;
  const perimeters: Array<{ scope: LeadScope; membershipId: string | null }> = [{ scope: leadScopeOf(access, action), membershipId: context.membershipId }];
  const observed = context.commercialView;
  if (observed) perimeters.push({ scope: roleAreaScope(observed.role, observed.areaPermissions, "leads", action), membershipId: observed.membershipId });
  return perimeters.every(
    (perimeter) => perimeter.scope === "all" || (perimeter.scope === "own" && perimeter.membershipId !== null && perimeter.membershipId === ownerMembershipId),
  );
}

/**
 * L'ambito che la scheda ha su «Conversazioni» (area `conversations`, che si può soltanto vedere), con la regola del server
 * (`conversationScope` in `apps/server/src/conversations/access.ts`): la piattaforma e il ruolo di sistema tutto, poi il ruolo;
 * nella «Guarda come…» il più stretto fra quello di chi guarda e quello della persona osservata. `all` sceglie «Di chi» e mostra
 * «Chi ha chiamato», `none` non ha la voce della rail. Il server decide.
 */
export function conversationScopeOf(access: Access): LeadScope {
  const { context } = access;
  const scopes = [access.isPlatform ? "all" : roleAreaScope(context.role, context.areaPermissions, "conversations", "view")];
  const observed = context.commercialView;
  if (observed) scopes.push(roleAreaScope(observed.role, observed.areaPermissions, "conversations", "view"));
  return scopes.reduce<LeadScope>(narrowest, "all");
}

/**
 * L'ambito che la scheda ha su «Attività» (area `activity`, che si può soltanto vedere; SEZ-8), con la regola del server (`activityScope`
 * in `apps/server/src/activity/access.ts`), la stessa di «Conversazioni»: `all` sceglie «Di chi», `none` non ha la voce della rail.
 */
export function activityScopeOf(access: Access): LeadScope {
  const { context } = access;
  const scopes = [access.isPlatform ? "all" : roleAreaScope(context.role, context.areaPermissions, "activity", "view")];
  const observed = context.commercialView;
  if (observed) scopes.push(roleAreaScope(observed.role, observed.areaPermissions, "activity", "view"));
  return scopes.reduce<LeadScope>(narrowest, "all");
}

export type TaskAction = "view" | "create" | "update";

/** L'ambito che un ruolo dà a un'azione dei Task (`roleScope` del server): il ruolo di sistema tutto; `create` con una lettura; `update` mai oltre `view`. */
function roleTaskScope(role: { isSystem: boolean } | null, areaPermissions: MyContext["areaPermissions"], action: TaskAction): LeadScope {
  if (role?.isSystem) return "all";
  const permissions = areaPermissions.tasks ?? {};
  const requested = scopeOf(permissions[action]);
  if (action === "view") return requested;
  const view = scopeOf(permissions.view);
  if (action === "create") return view === "none" || requested === "none" ? "none" : "all";
  return SCOPE_RANK[requested] <= SCOPE_RANK[view] ? requested : view;
}

type TaskPerimeter = { scope: LeadScope; membershipId: string | null };

/**
 * I perimetri dei Task che il server applica a una richiesta della scheda (`perimetersOf` in `apps/server/src/tasks/access.ts`):
 * quello di chi guarda (la piattaforma tutto) e, nella «Guarda come…», anche quello della persona osservata, dal suo ruolo e dai
 * suoi permessi che il contesto della vista porta; `own` vuol dire assegnati a quella persona.
 */
function taskPerimetersOf(access: Access, action: TaskAction): TaskPerimeter[] {
  const { context } = access;
  const own: TaskPerimeter = access.isPlatform
    ? { scope: "all", membershipId: null }
    : { scope: roleTaskScope(context.role, context.areaPermissions, action), membershipId: context.membershipId };
  const observed = context.commercialView;
  return observed ? [own, { scope: roleTaskScope(observed.role, observed.areaPermissions, action), membershipId: observed.membershipId }] : [own];
}

/**
 * L'ambito che la scheda ha su un'azione dei Task (area `tasks`), con la regola del server (`taskScope` in
 * `apps/server/src/tasks/access.ts`): il più stretto fra quello di chi guarda e, nella «Guarda come…», quello della persona
 * osservata. Serve a mostrare «Richiamo», «Richiama dopo» e «Sposta»: il server decide.
 */
export function taskScopeOf(access: Access, action: TaskAction): LeadScope {
  return taskPerimetersOf(access, action).reduce<LeadScope>((scope, perimeter) => (SCOPE_RANK[perimeter.scope] < SCOPE_RANK[scope] ? perimeter.scope : scope), "all");
}

/**
 * Se la scheda può vedere o cambiare un Task assegnato a questa persona (`canActOnTask` del server): ogni perimetro lo deve
 * permettere, `all` sempre, `own` soltanto per i Task della persona di quel perimetro.
 */
export function canActOnTaskOf(access: Access, action: Exclude<TaskAction, "create">, assigneeMembershipId: string): boolean {
  return taskPerimetersOf(access, action).every(
    (perimeter) => perimeter.scope === "all" || (perimeter.scope === "own" && perimeter.membershipId !== null && perimeter.membershipId === assigneeMembershipId),
  );
}

export type OpportunityAction = "view" | "update";

/**
 * I perimetri delle Opportunità che il server applica a una richiesta della scheda (`perimetersOf` in
 * `apps/server/src/opportunities/query.ts`): quello di chi guarda (la piattaforma tutto) e, nella «Guarda come…», quello della
 * persona osservata; `update` mai oltre `view`, `own` vuol dire le Opportunità che quella persona segue.
 */
function opportunityPerimetersOf(access: Access, action: OpportunityAction): TaskPerimeter[] {
  const { context } = access;
  const own: TaskPerimeter = access.isPlatform
    ? { scope: "all", membershipId: null }
    : { scope: roleAreaScope(context.role, context.areaPermissions, "opportunities", action), membershipId: context.membershipId };
  const observed = context.commercialView;
  return observed ? [own, { scope: roleAreaScope(observed.role, observed.areaPermissions, "opportunities", action), membershipId: observed.membershipId }] : [own];
}

/**
 * L'ambito che la scheda ha sulle Opportunità (area `opportunities`), con la regola del server (`opportunityScope`): il più stretto
 * fra quello di chi guarda e, nella «Guarda come…», quello della persona osservata. `view` decide la voce della rail e il riquadro
 * della scheda, `all` sceglie «Di chi»; `update: all` riapre un'Opportunità chiusa. Il server decide.
 */
export function opportunityScopeOf(access: Access, action: OpportunityAction): LeadScope {
  return opportunityPerimetersOf(access, action).reduce<LeadScope>((scope, perimeter) => narrowest(scope, perimeter.scope), "all");
}

/**
 * Se la scheda può vedere o cambiare un'Opportunità seguita da questa persona (`canActOnOpportunity` del server): ogni perimetro lo
 * deve permettere, `all` sempre, `own` soltanto per le Opportunità che segue la persona di quel perimetro.
 */
export function canActOnOpportunityOf(access: Access, action: OpportunityAction, assigneeMembershipId: string): boolean {
  return opportunityPerimetersOf(access, action).every(
    (perimeter) => perimeter.scope === "all" || (perimeter.scope === "own" && perimeter.membershipId !== null && perimeter.membershipId === assigneeMembershipId),
  );
}

/** L'accesso della scheda nelle pagine di un'azienda; segue gli aggiornamenti del contesto nella cache. */
export function useAccess(): Access {
  const { tab, myContext } = useRouteContext({ from: "/autenticato/guscio/azienda" });
  const { data } = useQuery(contextQuery(tab));
  const context = data ?? myContext;
  return useMemo(() => accessOf(context), [context]);
}

/**
 * La scheda convalidata da una guardia, anche fuori dalle pagine di un'azienda (il guscio sta pure sulle pagine della
 * piattaforma), con la sua Vista Commerciale, che ogni richiesta deve portare. `null` come per `useOptionalAccess`.
 * Resta lo stesso oggetto finché la scheda non cambia: la guardia la rilegge dallo Storage a ogni navigazione (un oggetto
 * nuovo), e chi dipende da lei, come la lettura in anticipo della shell, non deve ripartire per questo.
 */
export function useOptionalTab(): TabContext | null {
  // `useMatch` con `from` legge la rotta dove sta, anche da un componente di un layout che la contiene.
  // Una guardia non riuscita non lascia il contesto: `?? null` lo tratta come assente.
  const company = useMatch({ from: "/autenticato/guscio/azienda", shouldThrow: false, structuralSharing: true, select: (match) => match.context.tab ?? null });
  const platform = useMatch({ from: "/autenticato/guscio/piattaforma", shouldThrow: false, structuralSharing: true, select: (match) => match.context.tab ?? null });
  return company ?? platform ?? null;
}

/**
 * Come `useAccess`, ma anche fuori dalle pagine di un'azienda: il guscio (rail, account, avviso di D13) sta
 * pure sulle pagine della piattaforma, dove la scheda può non avere un'azienda. `null` quando nessuna guardia
 * ha convalidato un'azienda: pagine della piattaforma senza azienda, oppure guardia fallita (pagina d'errore
 * dentro il guscio).
 */
export function useOptionalAccess(): Access | null {
  const company = useMatch({ from: "/autenticato/guscio/azienda", shouldThrow: false, select: (match) => match.context.myContext ?? null });
  const platform = useMatch({ from: "/autenticato/guscio/piattaforma", shouldThrow: false, select: (match) => match.context.myContext ?? null });
  const guarded = company ?? platform ?? null;
  const tab = useOptionalTab();
  // La cache resta osservata anche qui, come in `useAccess`: un contesto riletto arriva al guscio, e un
  // rifiuto arriva al gestore degli errori delle query (D17 dopo il primo caricamento). La scheda è quella della
  // guardia, con la sua Vista Commerciale: una rilettura manda gli stessi header della guardia.
  const queries: Array<ReturnType<typeof contextQuery>> = guarded && tab ? [contextQuery(tab)] : [];
  const [live] = useQueries({ queries });
  const context = live?.data ?? guarded;
  return useMemo(() => (context ? accessOf(context) : null), [context]);
}

/**
 * Di chi è il menu della scheda (voci della rail, sezioni delle Impostazioni, pagine che si aprono): di chi guarda,
 * oppure, con «Guarda come…» (la Vista Commerciale), della persona scelta, di qualunque ruolo (decisioni del product
 * owner del 2 ottobre 2026: «In Vista Commerciale il menu è quello del Commerciale scelto», poi la vista su ogni ruolo).
 * `access` è l'accesso da cui si calcola il menu, `null` quando non c'è un'azienda (o, nella vista, finché il contesto
 * non porta i permessi della persona osservata: nessuna voce, mai quelle di chi guarda); `platformAdmin` porta le voci
 * della piattaforma, che nella vista non ci sono mai; `observed` dice che il menu è della persona osservata. Serve soltanto a decidere che cosa mostrare: i comandi nelle pagine e il server
 * restano di chi guarda, perché la vista non è impersonazione.
 */
export type MenuSubject = { access: Access | null; platformAdmin: boolean; observed: boolean };

/**
 * L'accesso della persona osservata con «Guarda come…», dai permessi che il server manda nel contesto della scheda
 * (`commercialView`): il suo ruolo, le sue funzioni effettive e i suoi permessi, nella stessa azienda. `null` fuori dalla
 * vista. Serve soltanto a costruire il suo menu.
 */
export function observedAccessOf(access: Access): Access | null {
  const observed = access.context.commercialView;
  if (!observed) return null;
  return accessOf({
    ...access.context,
    kind: "membership",
    membershipId: observed.membershipId,
    firstName: null,
    lastName: null,
    displayName: observed.displayName,
    role: observed.role,
    features: observed.features,
    areaPermissions: observed.areaPermissions,
    commercialView: null,
  });
}

/**
 * Il menu della scheda: fuori dalla vista quello di chi guarda; nella vista (la fascia la mostra, `tab.commercialView`)
 * quello della persona scelta, soltanto se il contesto porta proprio i suoi permessi. Altrimenti, per esempio con un
 * contesto letto senza la vista, nessuna voce: il menu della vista non è mai quello di chi guarda.
 */
export function menuSubjectOf(me: Me, access: Access | null, tab: TabContext | null): MenuSubject {
  const view = tab?.commercialView;
  if (!view) return { access, platformAdmin: me.isPlatformAdmin, observed: false };
  const observed = access && access.context.commercialView?.membershipId === view.membershipId ? observedAccessOf(access) : null;
  return { access: observed, platformAdmin: false, observed: true };
}

/** Il menu della scheda (`menuSubjectOf`) dentro il guscio, anche sulle pagine della piattaforma. */
export function useMenuSubject(): MenuSubject {
  const me = useMe();
  const access = useOptionalAccess();
  const tab = useOptionalTab();
  return useMemo(() => menuSubjectOf(me, access, tab), [me, access, tab]);
}

/**
 * Come la persona compare in questa scheda: in un'azienda con il nome del team (lo cambia chi gestisce il
 * team), nel contesto di piattaforma e fuori da ogni azienda con il nome dell'account; mai un nome vuoto.
 */
export function nameInTab(me: Me, access: Access | null): string {
  const inCompany = access && !access.isPlatform ? access.context.displayName : null;
  return (inCompany ?? me.name).trim() || me.email;
}

/**
 * Nome e cognome della persona in questa scheda, presi dallo stesso posto di `nameInTab`: del team in un'azienda,
 * dell'account altrove. `null` per un account nato prima che fossero due campi, finché non li scrive.
 */
function namePartsInTab(me: Me, access: Access | null): { firstName: string | null; lastName: string | null } {
  if (access && !access.isPlatform) return { firstName: access.context.firstName, lastName: access.context.lastName };
  return { firstName: me.firstName, lastName: me.lastName };
}

/** Il nome con cui la Home saluta («Ciao, Renato»): il nome senza il cognome; se manca, il nome intero. */
export function firstNameInTab(me: Me, access: Access | null): string {
  return namePartsInTab(me, access).firstName?.trim() || nameInTab(me, access);
}

/**
 * Le iniziali di nome e cognome (al massimo due), per il cerchio del pulsante dell'account; senza nome, quelle delle
 * prime due parole del nome intero.
 */
export function initialsInTab(me: Me, access: Access | null): string {
  const { firstName, lastName } = namePartsInTab(me, access);
  const words = firstName?.trim() ? [firstName, lastName ?? ""] : nameInTab(me, access).split(/\s+/).slice(0, 2);
  return words
    .map((word) => Array.from(word.trim())[0] ?? "")
    .join("")
    .toLocaleUpperCase();
}
